<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">
              <i class="bi bi-pencil-square me-2"></i>
              Editar Script #{{ scriptId }}
            </h1>
            <p class="text-muted mb-0">
              Modifica la configuración del script de monitoreo
            </p>
          </div>
          <router-link
            to="/scripts"
            class="btn btn-outline-secondary"
          >
            <i class="bi bi-arrow-left me-1"></i>
            Volver
          </router-link>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="text-muted mt-2">Cargando información del script...</p>
    </div>

    <!-- Edit Form -->
    <div v-else-if="script" class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Configuración del Script</h5>
          </div>
          <div class="card-body">
            <!-- Warning if running -->
            <div v-if="script.status === 'running'" class="alert alert-warning mb-4">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Advertencia:</strong> Este script está en ejecución. Debes detenerlo antes de poder editar su configuración.
            </div>

            <form @submit.prevent="handleSubmit">
              <!-- Client Information (Read-only) -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-person-circle me-2"></i>
                  Cliente
                  <span class="badge bg-secondary ms-2">No editable</span>
                </h6>

                <div class="alert alert-info">
                  <strong><i class="bi bi-info-circle me-1"></i> Cliente:</strong><br>
                  <div class="mt-2">
                    <small>
                      <strong>Nombre:</strong> {{ script.client_name }}<br>
                      <strong>Base de datos:</strong> {{ script.geotab_database }}
                    </small>
                  </div>
                  <div class="form-text mt-2">
                    El cliente no puede ser modificado después de crear el script.
                  </div>
                </div>
              </div>

              <!-- Script-specific Credentials (Optional) -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-key me-2"></i>
                  Credenciales Específicas del Script (Opcional)
                </h6>

                <div class="alert alert-info small">
                  <i class="bi bi-info-circle me-1"></i>
                  Si especificas credenciales aquí, se usarán en lugar de las credenciales del cliente.
                  Esto es útil cuando necesitas usar diferentes usuarios de MyGeotab para diferentes scripts del mismo cliente.
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="script_user" class="form-label">
                      Usuario MyGeotab
                    </label>
                    <input
                      id="script_user"
                      v-model="formData.user"
                      type="text"
                      class="form-control"
                      placeholder="usuario@empresa.com"
                      :disabled="script.status === 'running'"
                    >
                    <div class="form-text">
                      Dejar vacío para usar el usuario del cliente
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label for="script_password" class="form-label">
                      Contraseña MyGeotab
                    </label>
                    <input
                      id="script_password"
                      v-model="formData.password"
                      type="password"
                      class="form-control"
                      placeholder="••••••••"
                      :disabled="script.status === 'running'"
                    >
                    <div class="form-text">
                      Dejar vacío para usar la contraseña del cliente
                    </div>
                  </div>
                </div>
              </div>

              <!-- Script Configuration -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-gear me-2"></i>
                  Configuración del Script
                </h6>

                <div class="row g-3">
                  <!-- Feeder Type (Read-only) -->
                  <div class="col-12">
                    <label for="feeder_type" class="form-label">
                      Tipo de Feeder
                      <span class="badge bg-secondary ms-2">No editable</span>
                    </label>
                    <input
                      id="feeder_type"
                      type="text"
                      class="form-control"
                      :value="getFeederTypeName(script.config?.feeder_type)"
                      readonly
                      disabled
                    >
                    <div class="form-text">
                      El tipo de feeder no puede ser modificado después de crear el script.
                    </div>
                  </div>

                  <!-- Interval -->
                  <div class="col-md-6">
                    <label for="interval" class="form-label">
                      Intervalo de Polling (segundos) <span class="text-danger">*</span>
                    </label>
                    <input
                      id="interval"
                      v-model.number="formData.config.interval"
                      type="number"
                      class="form-control"
                      min="2"
                      max="300"
                      required
                      :disabled="script.status === 'running'"
                    >
                    <div class="form-text">
                      Entre 2 y 300 segundos. Recomendado: 10 segundos
                    </div>
                  </div>

                  <!-- Single Device -->
                  <div class="col-md-6">
                    <label for="single_device" class="form-label">
                      Dispositivo Específico (Opcional)
                    </label>
                    <input
                      id="single_device"
                      v-model="formData.config.single_device"
                      type="text"
                      class="form-control"
                      placeholder="Ej: b1234"
                      :disabled="script.status === 'running'"
                    >
                    <div class="form-text">
                      Dejar vacío para monitorear todos los dispositivos
                    </div>
                  </div>

                  <!-- Dry Run -->
                  <div class="col-md-6">
                    <div class="form-check form-switch">
                      <input
                        id="dryrun"
                        v-model="formData.config.dryrun"
                        type="checkbox"
                        class="form-check-input"
                        role="switch"
                        :disabled="script.status === 'running'"
                      >
                      <label for="dryrun" class="form-check-label">
                        Modo Prueba (Dry Run)
                      </label>
                    </div>
                    <div class="form-text">
                      No envía datos reales, solo simula el proceso
                    </div>
                  </div>

                  <!-- Server URL -->
                  <div class="col-12">
                    <label for="server" class="form-label">
                      URL del Servidor (Opcional)
                    </label>
                    <input
                      id="server"
                      v-model="formData.config.server"
                      type="url"
                      class="form-control"
                      placeholder="https://api.example.com/endpoint"
                      :disabled="script.status === 'running'"
                    >
                    <div class="form-text">
                      URL personalizada para envío de datos. Dejar vacío para usar configuración por defecto
                    </div>
                  </div>

                  <!-- Start Date -->
                  <div class="col-md-6">
                    <label for="start_date" class="form-label">
                      Fecha de Inicio (Opcional)
                    </label>
                    <input
                      id="start_date"
                      v-model="formData.config.start_date"
                      type="datetime-local"
                      class="form-control"
                      :disabled="script.status === 'running'"
                    >
                    <div class="form-text">
                      Para reproducir datos históricos desde una fecha específica
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-sticky me-2"></i>
                  Notas Adicionales
                </h6>

                <div class="mb-3">
                  <label for="notes" class="form-label">
                    Notas (Opcional)
                  </label>
                  <textarea
                    id="notes"
                    v-model="formData.notes"
                    class="form-control"
                    rows="3"
                    placeholder="Descripción o notas sobre este script..."
                    :disabled="script.status === 'running'"
                  ></textarea>
                </div>
              </div>

              <!-- Submit Buttons -->
              <div class="d-flex justify-content-end gap-2">
                <router-link
                  to="/scripts"
                  class="btn btn-outline-secondary"
                  :class="{'disabled': submitting}"
                >
                  Cancelar
                </router-link>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="submitting || script.status === 'running'"
                >
                  <span v-if="submitting">
                    <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Guardando...
                  </span>
                  <span v-else>
                    <i class="bi bi-save me-1"></i>
                    Guardar Cambios
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Help Sidebar -->
      <div class="col-lg-4">
        <div class="card bg-light">
          <div class="card-header bg-primary text-white">
            <h6 class="mb-0">
              <i class="bi bi-info-circle me-1"></i>
              Información
            </h6>
          </div>
          <div class="card-body">
            <h6>Estado del Script</h6>
            <p class="small">
              <span :class="getStatusBadgeClass(script.status)">
                {{ getStatusText(script.status) }}
              </span>
            </p>

            <h6 class="mt-3">Campos no editables:</h6>
            <ul class="small">
              <li>
                <strong>Cliente:</strong> No se puede cambiar el cliente asociado al script.
              </li>
              <li>
                <strong>Tipo de Feeder:</strong> El sistema de destino no puede ser modificado.
              </li>
            </ul>

            <h6 class="mt-3">Campos editables:</h6>
            <ul class="small">
              <li>
                <strong>Credenciales del script:</strong> Usuario y contraseña específicos para este script (opcional).
              </li>
              <li>
                <strong>Intervalo:</strong> Frecuencia de consulta a MyGeotab.
              </li>
              <li>
                <strong>Dispositivo específico:</strong> Filtro para monitorear solo un vehículo.
              </li>
              <li>
                <strong>Modo prueba:</strong> Habilita/deshabilita el modo de prueba.
              </li>
              <li>
                <strong>URL del servidor:</strong> Endpoint personalizado para envío de datos.
              </li>
              <li>
                <strong>Fecha de inicio:</strong> Punto de partida para reproducción de datos.
              </li>
              <li>
                <strong>Notas:</strong> Información adicional sobre el script.
              </li>
            </ul>

            <div class="alert alert-warning mt-3 small">
              <i class="bi bi-exclamation-triangle me-1"></i>
              <strong>Importante:</strong> El script debe estar detenido para poder editar su configuración.
            </div>

            <div v-if="script.pid" class="alert alert-info mt-3 small">
              <i class="bi bi-info-circle me-1"></i>
              <strong>PID actual:</strong> {{ script.pid }}
            </div>
          </div>
        </div>

        <!-- Script Actions -->
        <div class="card mt-3">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="bi bi-tools me-1"></i>
              Acciones
            </h6>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <router-link
                :to="`/scripts/${scriptId}/logs`"
                class="btn btn-outline-primary btn-sm"
              >
                <i class="bi bi-file-text me-1"></i>
                Ver Logs
              </router-link>

              <button
                v-if="script.status === 'running'"
                type="button"
                class="btn btn-outline-warning btn-sm"
                @click="stopScript"
              >
                <i class="bi bi-pause-circle me-1"></i>
                Detener Script
              </button>

              <button
                v-if="script.status === 'stopped' || script.status === 'failed'"
                type="button"
                class="btn btn-outline-success btn-sm"
                @click="restartScript"
              >
                <i class="bi bi-arrow-clockwise me-1"></i>
                Reiniciar Script
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else class="text-center py-5">
      <i class="bi bi-exclamation-triangle fs-1 text-warning"></i>
      <p class="text-muted mt-2">Error al cargar el script</p>
      <router-link to="/scripts" class="btn btn-primary">
        Volver a Scripts
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useScriptStore } from '../../stores/scripts'

const router = useRouter()
const route = useRoute()
const scriptStore = useScriptStore()

// State
const scriptId = ref(parseInt(route.params.id))
const script = ref(null)
const loading = ref(true)
const submitting = ref(false)
const errors = ref({})

const formData = ref({
  user: null,
  password: null,
  config: {
    interval: 10,
    dryrun: false,
    single_device: null,
    server: null,
    start_date: null
  },
  notes: ''
})

// Methods
const loadScript = async () => {
  loading.value = true
  try {
    script.value = await scriptStore.fetchScript(scriptId.value)

    // Populate form with script data
    if (script.value) {
      formData.value = {
        user: script.value.user || null,
        password: script.value.password || null,
        config: {
          interval: script.value.config?.interval || 10,
          dryrun: script.value.config?.dryrun || false,
          single_device: script.value.config?.single_device || null,
          server: script.value.config?.server || null,
          start_date: script.value.config?.start_date || null
        },
        notes: script.value.notes || ''
      }
    }
  } catch (error) {
    console.error('Error loading script:', error)
  } finally {
    loading.value = false
  }
}

const validateForm = () => {
  errors.value = {}

  if (formData.value.config.interval < 2 || formData.value.config.interval > 300) {
    errors.value.interval = 'El intervalo debe estar entre 2 y 300 segundos'
    return false
  }

  return true
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  if (script.value.status === 'running') {
    alert('No se puede editar un script en ejecución. Deténlo primero.')
    return
  }

  submitting.value = true

  try {
    // Clean up config - remove empty values
    const config = { ...formData.value.config }
    if (!config.single_device) delete config.single_device
    if (!config.server) delete config.server
    if (!config.start_date) delete config.start_date

    const scriptData = {
      user: formData.value.user || null,
      password: formData.value.password || null,
      config: config,
      notes: formData.value.notes || null
    }

    await scriptStore.updateScript(scriptId.value, scriptData)

    // Redirect to scripts list
    router.push('/scripts')
  } catch (error) {
    console.error('Error updating script:', error)
    // Error is handled by the store and notification
  } finally {
    submitting.value = false
  }
}

const stopScript = async () => {
  if (confirm('¿Estás seguro de que deseas detener este script?')) {
    try {
      await scriptStore.stopScript(scriptId.value)
      await loadScript() // Reload to update status
    } catch (error) {
      console.error('Error stopping script:', error)
    }
  }
}

const restartScript = async () => {
  if (confirm('¿Estás seguro de que deseas reiniciar este script?')) {
    try {
      await scriptStore.restartScript(scriptId.value)
      await loadScript() // Reload to update status
    } catch (error) {
      console.error('Error restarting script:', error)
    }
  }
}

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'badge bg-secondary',
    running: 'badge bg-success',
    stopped: 'badge bg-warning',
    failed: 'badge bg-danger',
    completed: 'badge bg-info'
  }
  return classes[status] || 'badge bg-secondary'
}

const getStatusText = (status) => {
  const texts = {
    pending: 'Pendiente',
    running: 'En Ejecución',
    stopped: 'Detenido',
    failed: 'Fallido',
    completed: 'Completado'
  }
  return texts[status] || status
}

const getFeederTypeName = (feederType) => {
  const names = {
    localizarte: 'Localizarte',
    pae: 'PAE'
  }
  return names[feederType] || feederType
}

// Lifecycle
onMounted(async () => {
  await loadScript()
})
</script>

<style scoped>
.form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #e9ecef;
  opacity: 1;
}
</style>
